/**
 * API client.
 *
 * One place that knows about the token, the base URL and the API's error
 * shape, so no component has to think about any of it.
 */
import type { ApiError } from '@lcc/shared';

const BASE = (import.meta.env.VITE_API_BASE ?? '') + '/api';
const TOKEN_KEY = 'lcc.token';

/**
 * Which club this console is for.
 *
 * Only needed before sign-in: once there is a token, the server takes the club
 * from it and ignores anything sent here. It matters at the login screen,
 * where there is nothing else to go on — with two clubs installed and no
 * slug, the server cannot tell which one is being signed in to and says so.
 *
 * Left unset for a single-club installation, where the server resolves the
 * only club there is. Set VITE_CLUB_SLUG per deployment, or serve the console
 * on a domain recorded against the club.
 *
 * `?club=<slug>` in the address overrides it, which is how one console on one
 * machine reaches a second club — the demo uses it, and so does support when
 * signing in to a club whose deployment is not up yet. It cannot widen
 * anybody's access: the server takes the club from the token the moment there
 * is one, and refuses a token and a conflicting slug together.
 */
function configuredClub(): string {
  try {
    const asked = new URLSearchParams(window.location.search).get('club');
    if (asked && asked.trim()) return asked.trim().toLowerCase();
  } catch {
    /* no window (tests, SSR) — fall through to the build-time value */
  }
  return (import.meta.env.VITE_CLUB_SLUG ?? '').trim();
}

const CLUB_SLUG = configuredClub();

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Per-field messages from a 422, keyed by field path. */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Field errors, when the API returned a validation failure. */
  get fieldErrors(): Record<string, string[]> {
    return this.status === 422 && this.details && typeof this.details === 'object'
      ? (this.details as Record<string, string[]>)
      : {};
  }
}

export const token = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (value: string) => localStorage.setItem(TOKEN_KEY, value),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const jwt = token.get();
  if (jwt) headers.authorization = `Bearer ${jwt}`;
  // Sending this alongside a token would be refused if they disagreed, so it
  // goes only when there is no token to disagree with.
  else if (CLUB_SLUG) headers['x-club'] = CLUB_SLUG;
  if (body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(BASE + path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const err = (payload as ApiError).error;
    // An expired session should land the user on the sign-in screen rather
    // than showing a wall of 401s behind the app.
    if (res.status === 401 && jwt) {
      token.clear();
      window.dispatchEvent(new Event('lcc:signed-out'));
    }
    throw new ApiRequestError(
      res.status,
      err?.code ?? 'error',
      err?.message ?? `Request failed (${res.status})`,
      err?.details,
    );
  }
  return payload as T;
}

/**
 * Fetches a file the API only serves to an authenticated caller.
 *
 * A plain <a href> cannot carry the bearer token, so the bytes come back as a
 * blob and are handed to the browser through an object URL. The URL is revoked
 * by the caller once the tab has it.
 */
export async function openAuthedFile(path: string): Promise<void> {
  const jwt = token.get();
  const res = await fetch(BASE + path, {
    headers: jwt ? { authorization: `Bearer ${jwt}` } : {},
  });
  if (!res.ok) {
    let message = `Could not open that file (${res.status})`;
    try {
      const body = (await res.json()) as ApiError;
      if (body.error?.message) message = body.error.message;
    } catch { /* a non-JSON error body is not worth reporting verbatim */ }
    throw new ApiRequestError(res.status, 'download_failed', message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener');
  if (!win) {
    // Pop-ups blocked: fall back to a download, which browsers allow.
    const a = document.createElement('a');
    a.href = url;
    a.download = path.split('/').pop() ?? 'waiver.pdf';
    a.click();
  }
  // Long enough for the new tab to have loaded the bytes.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Builds `?a=1&b=2`, dropping empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}
